const WISHLIST_KEY = 'shopify_wishlist';

class Wishlist {
  constructor() {
    this.currentPage = 1;
    this.itemsPerPage = 12;
    this.items = this.getItems();
    this.initButtons();
    
    // Listen for cross-tab updates or internal updates
    window.addEventListener('storage', (e) => {
      if (e.key === WISHLIST_KEY) {
        this.items = this.getItems();
        this.updateButtons();
      }
    });

    document.addEventListener('wishlist:updated', () => {
      this.updateButtons();
    });
  }

  getItems() {
    const data = localStorage.getItem(WISHLIST_KEY);
    return data ? JSON.parse(data) : [];
  }

  setItems(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    this.items = items;
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  toggle(handle) {
    let items = this.getItems();
    if (items.includes(handle)) {
      items = items.filter(i => i !== handle);
    } else {
      items.push(handle);
    }
    this.setItems(items);
  }

  contains(handle) {
    return this.getItems().includes(handle);
  }

  initButtons() {
    // We use event delegation on the document for dynamically added product cards
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.wishlist-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.dataset.productHandle;
        if (handle) {
          this.toggle(handle);
        }
      }
    });
    this.updateButtons();
  }

  updateButtons() {
    const buttons = document.querySelectorAll('.wishlist-btn');
    const items = this.getItems();
    buttons.forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (items.includes(handle)) {
        btn.classList.add('wishlist-btn--active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('wishlist-btn--active');
        btn.setAttribute('aria-pressed', 'false');
        
        // If we are on the wishlist page and an item was removed, reload the current page
        const grid = btn.closest('#wishlist-grid');
        if (grid) {
          const loader = document.getElementById('wishlist-loading');
          const emptyMsg = document.getElementById('wishlist-empty');
          this.loadWishlistPage(grid, loader, emptyMsg);
        }
      }
    });
  }

  async loadWishlistPage(gridContainer, loadingIndicator, emptyMessage) {
    const allItems = this.getItems();
    
    if (allItems.length === 0) {
      if (loadingIndicator) loadingIndicator.style.display = 'none';
      if (emptyMessage) emptyMessage.style.display = 'block';
      if (gridContainer) gridContainer.innerHTML = '';
      const paginationContainer = document.getElementById('wishlist-pagination');
      if (paginationContainer) paginationContainer.innerHTML = '';
      return;
    }

    if (emptyMessage) emptyMessage.style.display = 'none';
    if (gridContainer) gridContainer.innerHTML = '';
    if (loadingIndicator) loadingIndicator.style.display = 'block';

    const totalItems = allItems.length;
    const totalPages = Math.ceil(totalItems / this.itemsPerPage);
    if (this.currentPage > totalPages) this.currentPage = totalPages;

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const pageItems = allItems.slice(startIndex, endIndex);

    const fetchPromises = pageItems.map(handle => 
      fetch(`/products/${handle}?section_id=product-card-template`)
        .then(res => res.text())
        .catch(err => {
          console.error('Error fetching product card for', handle, err);
          return '';
        })
    );

    const htmlResults = await Promise.all(fetchPromises);
    
    if (loadingIndicator) loadingIndicator.style.display = 'none';
    
    if (gridContainer) {
      const validItems = [];
      const invalidHandles = [];
      
      htmlResults.forEach((html, index) => {
        if (!html) {
          invalidHandles.push(pageItems[index]);
          return;
        }
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        const sectionDiv = wrapper.firstElementChild;
        if (sectionDiv && sectionDiv.querySelector('.wishlist-item-wrapper')) {
           gridContainer.appendChild(sectionDiv);
           validItems.push(pageItems[index]);
        } else {
           invalidHandles.push(pageItems[index]);
        }
      });
      
      // Cleanup invalid items from storage
      if (invalidHandles.length > 0) {
        const newAllItems = allItems.filter(handle => !invalidHandles.includes(handle));
        this.setItems(newAllItems);
        // reload grid now that bad items are removed
        return this.loadWishlistPage(gridContainer, loadingIndicator, emptyMessage);
      }
      
      this.updateButtons();
      this.renderPagination(totalItems);
    }
  }

  renderPagination(totalItems) {
    const paginationContainer = document.getElementById('wishlist-pagination');
    if (!paginationContainer) return;
    
    const totalPages = Math.ceil(totalItems / this.itemsPerPage);
    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      return;
    }

    const prevIcon = `<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const nextIcon = `<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 11L6.5 6L1.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    let html = `
    <nav class="pagination" aria-label="Pagination">
      <ul class="pagination__list" role="list">
    `;

    // Previous button
    if (this.currentPage > 1) {
      html += `
        <li class="pagination__item">
          <a href="#" class="pagination__link pagination__link--arrow wishlist-page-btn" data-page="${this.currentPage - 1}" aria-label="Previous">
            ${prevIcon}
          </a>
        </li>
      `;
    } else {
      html += `
        <li class="pagination__item">
          <span class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">
            ${prevIcon}
          </span>
        </li>
      `;
    }

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (i === this.currentPage) {
        html += `
          <li class="pagination__item">
            <span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${i}</span>
          </li>
        `;
      } else {
        html += `
          <li class="pagination__item">
            <a href="#" class="pagination__link pagination__link--page wishlist-page-btn" data-page="${i}" aria-label="Page ${i}">${i}</a>
          </li>
        `;
      }
    }

    // Next button
    if (this.currentPage < totalPages) {
      html += `
        <li class="pagination__item">
          <a href="#" class="pagination__link pagination__link--arrow wishlist-page-btn" data-page="${this.currentPage + 1}" aria-label="Next">
            ${nextIcon}
          </a>
        </li>
      `;
    } else {
      html += `
        <li class="pagination__item">
          <span class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">
            ${nextIcon}
          </span>
        </li>
      `;
    }

    html += `
      </ul>
    </nav>
    `;

    paginationContainer.innerHTML = html;

    // Attach event listeners
    const pageButtons = paginationContainer.querySelectorAll('.wishlist-page-btn');
    pageButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.currentPage = parseInt(btn.dataset.page);
        
        const grid = document.getElementById('wishlist-grid');
        const loader = document.getElementById('wishlist-loading');
        const emptyMsg = document.getElementById('wishlist-empty');
        this.loadWishlistPage(grid, loader, emptyMsg);
        
        // Scroll to top of grid
        if (grid) {
          const offsetTop = grid.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
      });
    });
  }
}

// Initialize global wishlist
window.ShopifyWishlist = new Wishlist();
